import { Controller } from '@hotwired/stimulus';
import { arrow, autoUpdate, computePosition, flip, limitShift, offset, shift } from '@floating-ui/dom';

/*
  * Tooltip controller using Floating UI for positioning.
  * Features:
  * - Customizable ID, content, placement, variant, and additional classes.
  * - Optional arrow element.
  * - Auto-updates position on scroll and resize.
  *
  * Usage:
  * Add data-controller="tooltip" to the target element.
  * Configure options using data attributes:
  * - data-tooltip-id: ID for the tooltip element (default: 'tooltip').
  * - data-tooltip-content: Content for the tooltip (default: element's title).
  * - data-tooltip-placement: Placement of the tooltip (default: 'top').
  * - data-tooltip-variant: Variant class for styling (default: 'default').
  * - data-tooltip-arrow: Boolean to include arrow (default: true).
  * - data-tooltip-additional-classes: Additional CSS classes for the tooltip.
  *
  * Methods:
  * - show(): Displays the tooltip.
  * - hide(): Hides the tooltip.
*/
export default class extends Controller {
  static values = {
    id: { type: String, default: 'tooltip' },
    content: String,
    placement: { type: String, default: 'top' },
    variant: { type: String, default: 'default' },
    arrow: { type: Boolean, default: true },
    additionalClasses: String,
  };

  initialize() {
    this.cleanup = null;
  }

  connect() {
    this.createTooltipTarget();
  }

  disconnect() {
    this.tooltipTarget.remove();
  }

  show() {
    this.tooltipTarget.dataset.show = true;

    /* This is essentially directly from the docs:
     * https://floating-ui.com/docs/tutorial
     * https://floating-ui.com/docs/computePosition
     * https://floating-ui.com/docs/autoUpdate
    */
    this.cleanup = autoUpdate(this.element, this.tooltipTarget, () => {
      computePosition(this.element, this.tooltipTarget, {
        placement: this.placementValue,
        middleware: [offset(5), flip(), shift({ limiter: limitShift() }), arrow({ element: this.tooltipArrow })],
      }).then(({ x, y, placement, middlewareData }) => {
        Object.assign(this.tooltipTarget.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
        // Accessing the data
        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        const staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[placement.split('-')[0]];

        Object.assign(this.tooltipArrow.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        });
      });
    });
  }

  hide() {
    delete this.tooltipTarget.dataset.show;
    if (typeof this.cleanup === 'function') {
      this.cleanup();
    }
  }

  createTooltipTarget() {
    this.tooltipTarget = document.getElementById(this.idValue);
    if (!this.tooltipTarget) {
      this.tooltipTarget = document.createElement('div');
      this.tooltipTarget.id = this.idValue;
      this.tooltipTarget.classList.add('tooltip');
      if (this.hasVariantValue && this.variantValue !== 'default') {
        this.tooltipTarget.classList.add(`tooltip-${this.variantValue}`);
      }
      if (this.hasAdditionalClassesValue) {
        this.tooltipTarget.classList.add(...this.additionalClassesValue.split(' '));
      }
      this.tooltipTarget.setAttribute('role', 'tooltip');
      this.tooltipTarget.innerText = this.hasContentValue ? this.contentValue : this.element.title || '';
      this.element.removeAttribute('title');
    }

    if (this.arrowValue) {
      this.tooltipArrow = this.tooltipTarget.querySelector('.tooltip-arrow');
      if (!this.tooltipArrow) {
        this.tooltipArrow = document.createElement('div');
        this.tooltipArrow.classList.add('tooltip-arrow');
        this.tooltipTarget.appendChild(this.tooltipArrow);
      }
    }

    document.body.appendChild(this.tooltipTarget);
  }
}
